/**
 * Headless playback engine: given an Episode, tracks the current node,
 * variables and history, and resolves choices/auto-advances. UI-agnostic so
 * the same logic drives the web player, future native clients and tooling
 * (path enumeration, analytics replays, editor previews).
 */
import {
  applyEffects,
  evaluate,
  initialVars,
  resolveTarget,
  seriesVars,
  targetNodeIds,
  type Choice,
  type EndingNode,
  type Episode,
  type StoryNode,
  type Vars,
  type VideoNode,
} from '@tales/story-schema';

export interface HistoryEntry {
  nodeId: string;
  choiceId?: string;
  at: number;
}

export interface PlayerState {
  nodeId: string;
  vars: Vars;
  history: HistoryEntry[];
  /** Ending ids reached in this session (for "Ending 2 of 3" UI). */
  reachedEndings: string[];
}

export interface PlayableNode {
  id: string;
  node: VideoNode | EndingNode;
  /** Choices currently visible after `when` filtering. */
  choices: Choice[];
  /** Node ids that may play next (for preloading). */
  nextCandidates: string[];
  isEnding: boolean;
}

export class StoryEngine {
  readonly episode: Episode;
  private state: PlayerState;
  private readonly now: () => number;

  private readonly seed: Vars;

  constructor(episode: Episode, opts: { now?: () => number; restore?: PlayerState; startAt?: string; seed?: Vars } = {}) {
    this.episode = episode;
    this.now = opts.now ?? (() => Date.now());
    this.seed = opts.seed ?? {};
    this.state = opts.restore ?? { nodeId: episode.start, vars: initialVars(episode, this.seed), history: [], reachedEndings: [] };
    if (!opts.restore) this.enter(opts.startAt && episode.nodes[opts.startAt] ? opts.startAt : episode.start);
  }

  getState(): PlayerState {
    return structuredClone(this.state);
  }

  /** Series-scoped variables to persist for the next episode. */
  carryOver(): Vars {
    return seriesVars(this.episode, this.state.vars);
  }

  restart(): PlayableNode {
    const keep = this.state.reachedEndings;
    this.state = { nodeId: this.episode.start, vars: initialVars(this.episode, this.seed), history: [], reachedEndings: keep };
    this.enter(this.episode.start);
    return this.current();
  }

  /** The node to show now. Switch nodes are resolved transparently. */
  current(): PlayableNode {
    const id = this.state.nodeId;
    const node = this.mustGet(id);
    if (node.type === 'switch') throw new Error(`engine stopped on switch node ${id}`);
    const choices = node.type === 'video' ? (node.choices ?? []).filter((c) => evaluate(c.when, this.state.vars)) : [];
    return {
      id,
      node,
      choices,
      nextCandidates: this.candidates(node),
      isEnding: node.type === 'ending',
    };
  }

  /** Apply a choice (by id) and advance. */
  choose(choiceId: string): PlayableNode {
    const cur = this.current();
    const choice = cur.choices.find((c) => c.id === choiceId);
    if (!choice) throw new Error(`choice ${choiceId} not available on ${cur.id}`);
    this.state.vars = applyEffects(this.state.vars, choice.effects, this.episode.variables);
    const last = this.state.history[this.state.history.length - 1];
    if (last && last.nodeId === cur.id) last.choiceId = choiceId;
    const to = resolveTarget(choice.to, this.state.vars);
    if (!to) throw new Error(`choice ${choiceId} on ${cur.id} resolved to nothing`);
    this.enter(to);
    return this.current();
  }

  /** Advance a node that has `next` (no choices). */
  advance(): PlayableNode {
    const cur = this.current();
    if (cur.node.type !== 'video' || !cur.node.next) throw new Error(`node ${cur.id} cannot auto-advance`);
    const to = resolveTarget(cur.node.next, this.state.vars);
    if (!to) throw new Error(`next on ${cur.id} resolved to nothing`);
    this.enter(to);
    return this.current();
  }

  /** The choice the timer falls back to, if any. */
  defaultChoice(): Choice | undefined {
    const cur = this.current();
    if (cur.node.type !== 'video' || !cur.node.defaultChoice) return undefined;
    const id = cur.node.defaultChoice;
    return cur.choices.find((c) => c.id === id) ?? cur.choices[0];
  }

  // ----- internals -----

  private enter(id: string): void {
    let node = this.mustGet(id);
    let guard = 0;
    // Resolve chains of switch nodes without recording them in history.
    while (node.type === 'switch') {
      if (++guard > 32) throw new Error('switch chain too long (cycle?)');
      this.state.vars = applyEffects(this.state.vars, node.effects, this.episode.variables);
      const to = resolveTarget(node.next, this.state.vars);
      if (!to) throw new Error(`switch ${id} resolved to nothing`);
      id = to;
      node = this.mustGet(id);
    }
    this.state.vars = applyEffects(this.state.vars, node.effects, this.episode.variables);
    this.state.nodeId = id;
    this.state.history.push({ nodeId: id, at: this.now() });
    if (node.type === 'ending' && !this.state.reachedEndings.includes(node.endingId)) this.state.reachedEndings.push(node.endingId);
  }

  private mustGet(id: string): StoryNode {
    const n = this.episode.nodes[id];
    if (!n) throw new Error(`unknown node ${id}`);
    return n;
  }

  /** Playable node ids reachable in one step (through switches), for preloading. */
  private candidates(node: StoryNode): string[] {
    if (node.type === 'ending') return [];
    const direct: string[] = [];
    if (node.type === 'switch') direct.push(...targetNodeIds(node.next));
    else {
      if (node.next) direct.push(...targetNodeIds(node.next));
      for (const c of node.choices ?? []) if (evaluate(c.when, this.state.vars)) direct.push(...targetNodeIds(c.to));
    }
    const out = new Set<string>();
    const seen = new Set<string>();
    const stack = [...direct];
    while (stack.length) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const n = this.episode.nodes[id];
      if (!n) continue;
      if (n.type === 'switch') stack.push(...targetNodeIds(n.next));
      else out.add(id);
    }
    return [...out];
  }
}

/** Enumerate every distinct path start→ending (bounded), for tooling and tests. */
export function enumeratePaths(episode: Episode, limit = 10_000): { path: string[]; choices: string[]; endingId: string }[] {
  const results: { path: string[]; choices: string[]; endingId: string }[] = [];
  const walk = (engine: StoryEngine, path: string[], choices: string[]) => {
    if (results.length >= limit) return;
    const cur = engine.current();
    const nextPath = [...path, cur.id];
    if (cur.isEnding) {
      results.push({ path: nextPath, choices, endingId: (cur.node as EndingNode).endingId });
      return;
    }
    if (nextPath.filter((p) => p === cur.id).length > 2) return; // crude cycle guard
    if (cur.choices.length === 0) {
      const next = new StoryEngine(episode, { restore: engine.getState() });
      next.advance();
      walk(next, nextPath, choices);
      return;
    }
    for (const c of cur.choices) {
      const next = new StoryEngine(episode, { restore: engine.getState() });
      next.choose(c.id);
      walk(next, nextPath, [...choices, `${cur.id}:${c.id}`]);
    }
  };
  walk(new StoryEngine(episode, { now: () => 0 }), [], []);
  return results;
}
